import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CheckCircle2, ShieldX, Star, X } from 'lucide-react';
import { useMemo, useState } from 'react';
import Card from '../../components/ui/Card';
import KpiTile from '../../components/ui/KpiTile';
import PageHeader from '../../components/ui/PageHeader';
import { adminApi, type AdminMember } from '../../services/adminApi';

const VERIFIED_LABEL: Record<number, string> = {
  0: 'Pending',
  1: 'Verified',
  2: 'Banned',
  3: 'Suspended',
};

const AdminUsersView = () => {
  const qc = useQueryClient();
  const [editing, setEditing] = useState<AdminMember | null>(null);

  const membersQuery = useQuery({
    queryKey: ['admin', 'members'],
    queryFn: async () => (await adminApi.listMembers()).data,
  });
  const rolesQuery = useQuery({
    queryKey: ['admin', 'roles'],
    queryFn: async () => (await adminApi.listRoles()).data,
  });

  const members = membersQuery.data ?? [];
  const counts = useMemo(() => {
    const c = { total: members.length, verified: 0, staff: 0, banned: 0 };
    for (const m of members) {
      if (m.verified === 1) c.verified++;
      if (m.tfstaff) c.staff++;
      if (m.verified === 2) c.banned++;
    }
    return c;
  }, [members]);

  return (
    <>
      <PageHeader
        title="User management"
        subtitle="Staff-only · grant roles, toggle staff/verification, set access levels."
      />

      <section className="grid grid-cols-1 gap-4 sm:grid-cols-4">
        <KpiTile label="Total members" value={String(counts.total)} />
        <KpiTile label="Verified" value={String(counts.verified)} Icon={CheckCircle2} />
        <KpiTile label="Staff" value={String(counts.staff)} Icon={Star} />
        <KpiTile label="Banned" value={String(counts.banned)} Icon={ShieldX} />
      </section>

      <Card className="mt-6" title={`Members · ${members.length}`}>
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="text-left text-xs uppercase tracking-wide text-slate-500">
              <tr>
                <th className="py-2 pr-4 font-medium">UID</th>
                <th className="py-2 pr-4 font-medium">Username</th>
                <th className="py-2 pr-4 font-medium">Email</th>
                <th className="py-2 pr-4 font-medium">Status</th>
                <th className="py-2 pr-4 font-medium">Staff</th>
                <th className="py-2 pr-4 font-medium">Level</th>
                <th className="py-2 pr-4 font-medium">Roles</th>
                <th className="py-2 pr-4 font-medium">Last login</th>
                <th />
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 text-slate-700">
              {members.map((m) => (
                <tr key={m.uid} className="hover:bg-slate-50">
                  <td className="py-2.5 pr-4 font-mono text-xs text-slate-500">{m.uid}</td>
                  <td className="py-2.5 pr-4 font-medium text-slate-800">{m.username}</td>
                  <td className="py-2.5 pr-4 text-slate-500">{m.email}</td>
                  <td className="py-2.5 pr-4">
                    <span
                      className={`inline-flex rounded-full px-2 py-0.5 text-[11px] font-medium ring-1 ${
                        m.verified === 1
                          ? 'bg-emerald-50 text-emerald-700 ring-emerald-200'
                          : m.verified === 2
                            ? 'bg-red-50 text-red-700 ring-red-200'
                            : 'bg-slate-100 text-slate-600 ring-slate-200'
                      }`}
                    >
                      {VERIFIED_LABEL[m.verified] ?? m.verified}
                    </span>
                  </td>
                  <td className="py-2.5 pr-4">
                    {m.tfstaff ? (
                      <span className="inline-flex items-center gap-1 text-xs font-medium text-tfs-primary">
                        <Star className="size-3.5 fill-current" /> Yes
                      </span>
                    ) : (
                      <span className="text-xs text-slate-400">—</span>
                    )}
                  </td>
                  <td className="py-2.5 pr-4 font-mono text-xs">{m.accessLevel}</td>
                  <td className="py-2.5 pr-4">
                    <div className="flex flex-wrap gap-1">
                      {m.roles.length === 0 && <span className="text-xs text-slate-400">—</span>}
                      {m.roles.map((r) => (
                        <span
                          key={r.id}
                          className="rounded-full bg-tfs-accent/10 px-2 py-0.5 text-[11px] font-medium text-tfs-primary"
                        >
                          {r.name}
                        </span>
                      ))}
                    </div>
                  </td>
                  <td className="py-2.5 pr-4 text-xs text-slate-500">
                    {m.lastLoggedIn
                      ? new Date(m.lastLoggedIn).toLocaleDateString(undefined, {
                          year: 'numeric',
                          month: 'short',
                          day: 'numeric',
                        })
                      : '—'}
                  </td>
                  <td className="py-2.5 pr-4">
                    <button
                      type="button"
                      onClick={() => setEditing(m)}
                      className="text-xs font-medium text-tfs-primary hover:underline"
                    >
                      Edit
                    </button>
                  </td>
                </tr>
              ))}
              {members.length === 0 && (
                <tr>
                  <td colSpan={9} className="py-8 text-center text-xs text-slate-400">
                    {membersQuery.isLoading ? 'Loading…' : 'No members.'}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>

      {editing && rolesQuery.data && (
        <EditMemberDialog
          member={editing}
          roles={rolesQuery.data}
          onClose={() => setEditing(null)}
          onSaved={() => {
            void qc.invalidateQueries({ queryKey: ['admin', 'members'] });
            setEditing(null);
          }}
        />
      )}
    </>
  );
};

interface EditDialogProps {
  member: AdminMember;
  roles: { id: number; name: string }[];
  onClose: () => void;
  onSaved: () => void;
}

const EditMemberDialog = ({ member, roles, onClose, onSaved }: EditDialogProps) => {
  const [verified, setVerified] = useState(member.verified);
  const [tfstaff, setTfstaff] = useState(member.tfstaff);
  const [accessLevel, setAccessLevel] = useState(member.accessLevel);
  const [roleIds, setRoleIds] = useState<number[]>(member.roles.map((r) => r.id));

  const saveMutation = useMutation({
    mutationFn: () =>
      adminApi.updateMember(member.uid, { verified, tfstaff, accessLevel, roleIds }),
    onSuccess: onSaved,
  });

  const toggleRole = (id: number) =>
    setRoleIds((prev) => (prev.includes(id) ? prev.filter((r) => r !== id) : [...prev, id]));

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4">
      <div className="w-full max-w-lg rounded-xl border border-slate-200 bg-white shadow-xl">
        <header className="flex items-center justify-between border-b border-slate-100 px-5 py-3.5">
          <div>
            <h2 className="text-sm font-semibold text-slate-800">Edit member</h2>
            <p className="text-xs text-slate-500">
              {member.username} · uid {member.uid}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-md p-1.5 text-slate-500 hover:bg-slate-100"
            aria-label="Close"
          >
            <X className="size-4" />
          </button>
        </header>

        <div className="space-y-5 px-5 py-5">
          <div>
            <label className="block text-xs font-medium uppercase tracking-wide text-slate-500">
              Verification
            </label>
            <select
              value={verified}
              onChange={(e) => setVerified(Number(e.target.value))}
              className="mt-1 w-full rounded-md border border-slate-300 px-2.5 py-1.5 text-sm"
            >
              <option value={0}>Pending</option>
              <option value={1}>Verified</option>
              <option value={2}>Banned</option>
              <option value={3}>Suspended</option>
            </select>
          </div>

          <div className="flex items-center justify-between rounded-md border border-slate-200 px-3 py-2.5">
            <div>
              <p className="text-sm font-medium text-slate-800">TFS staff</p>
              <p className="text-xs text-slate-500">Grants access to admin pages.</p>
            </div>
            <input
              type="checkbox"
              checked={tfstaff}
              onChange={(e) => setTfstaff(e.target.checked)}
              className="size-4"
            />
          </div>

          <div>
            <label className="block text-xs font-medium uppercase tracking-wide text-slate-500">
              Access level (0–99)
            </label>
            <input
              type="number"
              min={0}
              max={99}
              value={accessLevel}
              onChange={(e) => setAccessLevel(Number(e.target.value))}
              className="mt-1 w-full rounded-md border border-slate-300 px-2.5 py-1.5 text-sm font-mono"
            />
          </div>

          <div>
            <label className="block text-xs font-medium uppercase tracking-wide text-slate-500">
              Roles
            </label>
            <div className="mt-2 flex flex-wrap gap-2">
              {roles.map((r) => (
                <button
                  type="button"
                  key={r.id}
                  onClick={() => toggleRole(r.id)}
                  className={`rounded-full px-2.5 py-1 text-xs font-medium ring-1 ${
                    roleIds.includes(r.id)
                      ? 'bg-tfs-primary/10 text-tfs-primary ring-tfs-primary/40'
                      : 'bg-slate-50 text-slate-600 ring-slate-200 hover:bg-slate-100'
                  }`}
                >
                  {r.name}
                </button>
              ))}
            </div>
          </div>

          {saveMutation.isError && (
            <p className="text-xs text-red-600">
              Save failed: {(saveMutation.error as Error)?.message ?? 'unknown error'}
            </p>
          )}
        </div>

        <footer className="flex items-center justify-end gap-2 border-t border-slate-100 px-5 py-3">
          <button
            type="button"
            onClick={onClose}
            className="rounded-md border border-slate-300 bg-white px-3 py-1.5 text-sm hover:bg-slate-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => saveMutation.mutate()}
            disabled={saveMutation.isPending}
            className="rounded-md bg-tfs-primary px-3 py-1.5 text-sm font-medium text-white hover:opacity-90 disabled:opacity-50"
          >
            {saveMutation.isPending ? 'Saving…' : 'Save'}
          </button>
        </footer>
      </div>
    </div>
  );
};

export default AdminUsersView;
